import React, { useState, useEffect, useCallback } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Fab,
  Chip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Palette as PaletteIcon,
  Remove as RemoveIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeletePhotoIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { FormulaPigmentacao } from '../../types';
import { apiService } from '../../services/api';

interface PigmentoFormula {
  pigmentoId: number;
  quantidade: number;
  unidade: string;
}

interface FormulaFormData {
  nome: string;
  corFinal: string;
  descricao: string;
  fotoAmostra: string;
  pigmentos: PigmentoFormula[];
}

const pigmentosDisponiveis = [
  { id: 1, nome: 'Azul Cobalto', cor: '#0047AB' },
  { id: 2, nome: 'Vermelho Carmim', cor: '#DC143C' },
  { id: 3, nome: 'Amarelo Cádmio', cor: '#FFF700' },
  { id: 4, nome: 'Verde Esmeralda', cor: '#50C878' },
  { id: 5, nome: 'Preto Marfim', cor: '#2D2D2D' },
  { id: 6, nome: 'Branco Titânio', cor: '#F8F8FF' },
];

// Componente otimizado para cada pigmento
const PigmentoRow = React.memo<{
  index: number;
  register: any;
  control: any;
  remove: (index: number) => void;
}>(({ index, register, control, remove }) => {

  const handleRemove = useCallback(() => {
    remove(index);
  }, [index, remove]);

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <Box sx={{ flex: '1 1 200px', minWidth: 150 }}>
          <Controller
            name={`pigmentos.${index}.pigmentoId`}
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Pigmento</InputLabel>
                <Select {...field} label="Pigmento">
                  {pigmentosDisponiveis.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            backgroundColor: p.cor,
                            borderRadius: '2px',
                            border: '1px solid #ccc',
                          }}
                        />
                        {p.nome}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Box>
        <Box sx={{ flex: '0 0 120px' }}>
          <TextField
            fullWidth
            label="Quantidade"
            type="number"
            {...register(`pigmentos.${index}.quantidade`)}
            inputProps={{ step: '0.01', min: '0' }}
          />
        </Box>
        <Box sx={{ flex: '0 0 100px' }}>
          <Controller
            name={`pigmentos.${index}.unidade`}
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Unidade</InputLabel>
                <Select {...field} label="Unidade">
                  <MenuItem value="%">%</MenuItem>
                </Select>
              </FormControl>
            )}
          />
        </Box>
        <Box sx={{ flex: '0 0 auto' }}>
          <IconButton color="error" onClick={handleRemove}>
            <RemoveIcon />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
});

PigmentoRow.displayName = 'PigmentoRow';

const Formulas: React.FC = () => {
  const [formulas, setFormulas] = useState<FormulaPigmentacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFormula, setEditingFormula] = useState<FormulaPigmentacao | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // React Hook Form
  const { register, control, handleSubmit, reset, setValue, formState: { errors }, setError, clearErrors } = useForm<FormulaFormData>({
    defaultValues: {
      nome: '',
      corFinal: '#000000',
      descricao: '',
      fotoAmostra: '',
      pigmentos: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'pigmentos',
  });

  const showMessage = useCallback((message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  useEffect(() => {
    loadFormulas();
  }, []);

  const loadFormulas = async () => {
    try {
      setLoading(true);
      const data = await apiService.getFormulas();
      setFormulas(data);
    } catch (error) {
      console.error('Erro ao carregar fórmulas:', error);
      showMessage('Erro ao carregar fórmulas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = useCallback((formula?: FormulaPigmentacao) => {
    if (formula) {
      setEditingFormula(formula);
      reset({
        nome: formula.nome,
        corFinal: formula.corFinal,
        descricao: formula.descricao,
        fotoAmostra: formula.fotoAmostra || '',
        pigmentos: formula.pigmentos.map(p => ({
          pigmentoId: p.pigmentoId,
          quantidade: p.quantidade,
          unidade: p.unidade,
        })),
      });
      setPhotoPreview(formula.fotoAmostra || '');
    } else {
      setEditingFormula(null);
      reset({
        nome: '',
        corFinal: '#000000',
        descricao: '',
        fotoAmostra: '',
        pigmentos: [],
      });
      setPhotoPreview('');
    }
    setDialogOpen(true);
  }, [reset]);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setEditingFormula(null);
  }, []);

  const handlePhotoUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        showMessage('Por favor, selecione apenas arquivos de imagem.', 'warning');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        showMessage('A imagem deve ter no máximo 5MB.', 'warning');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target?.result as string;
        setValue('fotoAmostra', base64String);
        setPhotoPreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  }, [setValue, showMessage]);

  const handleRemovePhoto = useCallback(() => {
    setValue('fotoAmostra', '');
    setPhotoPreview('');
  }, [setValue]);

  const handleAddPigmento = useCallback(() => {
    append({
      pigmentoId: 1,
      quantidade: 0,
      unidade: '%',
    });
  }, [append]);

  const onSubmit = async (data: FormulaFormData) => {
    clearErrors();

    let hasErrors = false;

    if (!data.nome || data.nome.trim() === '') {
      setError('nome', { type: 'required', message: 'Nome da fórmula é obrigatório' });
      hasErrors = true;
    }

    // Validação da soma das porcentagens
    const somaPercentuais = data.pigmentos
      .filter(p => p.unidade === '%')
      .reduce((acc, p) => acc + Number(p.quantidade), 0);

    if (somaPercentuais > 100) {
      showMessage(`A soma das porcentagens (${somaPercentuais.toFixed(2)}%) não pode ultrapassar 100%`, 'error');
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    try {
      const formulaData = {
        ...data,
        pigmentos: data.pigmentos.map(p => ({
          pigmentoId: Number(p.pigmentoId),
          quantidade: Number(p.quantidade),
          unidade: p.unidade,
        })),
      };

      if (editingFormula) {
        await apiService.updateFormula(editingFormula.id, formulaData);
        showMessage('Fórmula atualizada com sucesso!', 'success');
      } else {
        await apiService.createFormula({
          ...formulaData,
          dataCriacao: new Date().toISOString().split('T')[0],
        });
        showMessage('Fórmula criada com sucesso!', 'success');
      }
      handleCloseDialog();
      loadFormulas();
    } catch (error: any) {
      console.error('Erro ao salvar fórmula:', error);
      const errorMessage = error?.message || error?.error || 'Erro ao salvar fórmula. Verifique os dados e tente novamente.';
      showMessage(errorMessage, 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir esta fórmula?')) {
      try {
        await apiService.deleteFormula(id);
        showMessage('Fórmula excluída com sucesso!', 'success');
        loadFormulas();
      } catch (error: any) {
        console.error('Erro ao excluir fórmula:', error);
        const errorMessage = error?.message || error?.error || 'Erro ao excluir fórmula. Tente novamente.';
        showMessage(errorMessage, 'error');
      }
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'nome', headerName: 'Nome', flex: 1, minWidth: 150 },
    {
      field: 'corFinal',
      headerName: 'Cor/Foto',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, width: '100%' }}>
          {params.row.fotoAmostra ? (
            <img
              src={params.row.fotoAmostra}
              alt={`Amostra ${params.row.nome}`}
              style={{
                width: 30,
                height: 30,
                objectFit: 'cover',
                borderRadius: '4px',
                border: '1px solid #ccc',
              }}
            />
          ) : (
            <Box
              sx={{
                width: 30,
                height: 30,
                backgroundColor: params.value,
                border: '1px solid #ccc',
                borderRadius: '4px',
              }}
            />
          )}
        </Box>
      ),
    },
    { field: 'descricao', headerName: 'Descrição', flex: 1, minWidth: 200 },
    {
      field: 'pigmentos',
      headerName: 'Pigmentos',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value.length} item(s)
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleOpenDialog(params.row)}
            color="primary"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row.id)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const FormulaCard: React.FC<{ formula: FormulaPigmentacao }> = ({ formula }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {formula.fotoAmostra ? (
            <Avatar
              sx={{
                mr: 2,
                width: 60,
                height: 60,
                borderRadius: 2,
              }}
              variant="rounded"
            >
              <img
                src={formula.fotoAmostra}
                alt={`Amostra ${formula.nome}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '8px',
                }}
              />
            </Avatar>
          ) : (
            <Avatar
              sx={{
                mr: 2,
                bgcolor: formula.corFinal,
                width: 60,
                height: 60,
                borderRadius: 2,
              }}
              variant="rounded"
            >
              <PaletteIcon />
            </Avatar>
          )}
          <Box>
            <Typography variant="h6" component="div">
              {formula.nome}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formula.descricao}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Pigmentos:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {formula.pigmentos.map((pigmento, index) => {
              const pigmentoInfo = pigmentosDisponiveis.find(p => p.id === pigmento.pigmentoId);
              return (
                <Chip
                  key={index}
                  label={`${pigmentoInfo?.nome || 'Desconhecido'}: ${pigmento.quantidade}${pigmento.unidade}`}
                  size="small"
                  sx={{
                    backgroundColor: pigmentoInfo?.cor || '#ccc',
                    color: pigmentoInfo?.cor === '#FFF700' ? '#000' : '#fff',
                  }}
                />
              );
            })}
          </Box>
        </Box>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={() => handleOpenDialog(formula)}
        >
          Editar
        </Button>
        <Button
          size="small"
          startIcon={<DeleteIcon />}
          color="error"
          onClick={() => handleDelete(formula.id)}
        >
          Excluir
        </Button>
      </CardActions>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
        >
          Fórmulas de Pigmentação
        </Typography>
        {!isMobile && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Nova Fórmula
          </Button>
        )}
      </Box>

      {isMobile ? (
        <>
          <Box sx={{ mb: 2 }}>
            {loading ? (
              <Typography>Carregando...</Typography>
            ) : (
              formulas.map((formula) => (
                <FormulaCard key={formula.id} formula={formula} />
              ))
            )}
          </Box>
          <Fab
            color="primary"
            aria-label="add"
            sx={{ position: 'fixed', bottom: 16, right: 16 }}
            onClick={() => handleOpenDialog()}
          >
            <AddIcon />
          </Fab>
        </>
      ) : (
        <Paper sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={formulas}
            columns={columns}
            loading={loading}
            pageSizeOptions={[5, 10, 25]}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />
        </Paper>
      )}

      {/* Dialog para criar/editar fórmula */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {editingFormula ? 'Editar Fórmula' : 'Nova Fórmula'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" type="submit">
                  Salvar
                </Button>
                <Button variant="outlined" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
                  <TextField
                    fullWidth
                    label="Nome da Fórmula"
                    required
                    {...register('nome')}
                    error={!!errors.nome}
                    helperText={errors.nome?.message}
                  />
                </Box>
                <Box sx={{ flex: '0 0 150px' }}>
                  <TextField
                    fullWidth
                    label="Cor Final"
                    type="color"
                    {...register('corFinal')}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </Box>

              <TextField
                fullWidth
                label="Descrição"
                multiline
                rows={2}
                {...register('descricao')}
              />

              {/* Seção de foto da amostra */}
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Foto da Amostra da Cor
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="photo-upload"
                      type="file"
                      onChange={handlePhotoUpload}
                    />
                    <label htmlFor="photo-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<PhotoCameraIcon />}
                      >
                        Selecionar Foto
                      </Button>
                    </label>
                    {photoPreview && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleRemovePhoto}
                        startIcon={<DeletePhotoIcon />}
                      >
                        Remover
                      </Button>
                    )}
                  </Box>
                  {photoPreview && (
                    <Box
                      sx={{
                        border: '2px dashed #ccc',
                        borderRadius: 2,
                        p: 2,
                        textAlign: 'center',
                      }}
                    >
                      <img
                        src={photoPreview}
                        alt="Preview da cor"
                        style={{
                          maxWidth: '200px',
                          maxHeight: '200px',
                          objectFit: 'contain',
                          borderRadius: '8px',
                        }}
                      />
                      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        Preview da foto da amostra
                      </Typography>
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Formatos aceitos: JPG, PNG, GIF. Tamanho máximo: 1MB
                  </Typography>
                </Box>
              </Box>

              {/* Seção de pigmentos */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Pigmentos</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddPigmento}
                  >
                    Adicionar Pigmento
                  </Button>
                </Box>
                {fields.map((field, index) => (
                  <PigmentoRow
                    key={field.id}
                    index={index}
                    register={register}
                    control={control}
                    remove={remove}
                  />
                ))}
              </Box>
            </Box>
          </DialogContent>
        </form>
      </Dialog>

      {/* Snackbar para mensagens de feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Formulas;
